import React from 'react';
import { Formik } from 'formik';
import { Alert, Col, Collapse, Divider, Row, Spin, Typography } from 'antd';
import { Form, FormItem, ResetButton, Select, SubmitButton, Switch } from 'formik-antd';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import { useSelector } from 'react-redux';
import IntlMessages from '../../../../layout/components/lang/IntlMessages';
import TitleWithAutomaticGeneration from '../../../../components/TitleWithAutomaticGeneration';
import mkMainAndOptionalTemplateFields from '../../../Template builder/utils/mkMainAndOptionalTemplateFields';
import Spinner from '../../../../components/Spinner';
import mkTemplateDynamicFormData from '../../../Content/utils/mkTemplateDynamicFormData';
import DynamicFormDataFields from '../../../Content/components/DynamicFormDataFields';



const { Title } = Typography;
const { Panel } = Collapse;

const FormEditLocalNotification = ({
  initialErrors,
  initialValues,
  templateFields,
  templateOptions,
  validationSchema,
  onSubmit,
  onCancel,
  isSubmitting,
}) => {
  const intl = useIntl();
  const { mainFields, optionalFields } = mkMainAndOptionalTemplateFields(templateFields);
  const language = useSelector(({ customise: { language } }) => language);

  if (!isEmpty(initialErrors)) {
    window.scrollTo(0, 0);
  }

  return (
    <Formik
      enableReinitialize
      isSubmitting
      initialErrors={initialErrors}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values, { resetForm }) => {
        onSubmit(mkTemplateDynamicFormData(values, language, templateFields), resetForm);
      }}
      onReset={async (values) => {
        onCancel(values);
      }}
    >
      {({ isValid, dirty, values, setFieldValue, resetForm }) => {
        const hasUnpublishedChanges = !moment(values?.published_at).isSame(moment(values?.updated_at));

        return (
          <Form layout="vertical" >
            <Spinner spinning={isSubmitting}>
              <Row gutter={16}>
                <Col md={12} xs={24}>
                  <Row gutter={16}>
                    <Col span={24}>
                      <FormItem
                        label={intl.formatMessage({ id: 'ui-general-template' })}
                        name='template_id'
                        required
                      >
                        <Select
                          loading={false}
                          allowClear
                          disabled
                          name="template_id"
                          options={templateOptions}
                          placeholder={intl.formatMessage({ id: 'ui-general-choose-template' })}
                        />
                      </FormItem>
                    </Col>

                    <Col span={24}>
                      <Collapse accordion className='hp-mb-24'>
                        <Panel
                          key="additional-info"
                          header={
                            <Title className='collapse-item-title' level={5}>
                              {intl.formatMessage({ id: 'ui-general-optional-data' })}
                            </Title>
                          }
                        >
                          <Row gutter={16}>
                            <Col span={24}>
                              <TitleWithAutomaticGeneration useAutomaticGeneration={false} />
                            </Col>
                          </Row>
                        </Panel>
                      </Collapse>
                    </Col>
                  </Row>

                  <Divider />
                  <Title className="hp-mb-24" level={4}><IntlMessages id="ui-general-template-data" /></Title>
                  {templateFields.length ? (
                    <Row className="hp-mb-24">
                      <Col span={12}>
                        <DynamicFormDataFields
                          setFieldValue={setFieldValue}
                          fields={mainFields}
                          values={values}
                          span={24}
                        />
                        {!!optionalFields.length && (
                          <Collapse>
                            <Panel
                              key="1"
                              header={
                                <Title level={5} className='collapse-item-title'>
                                  <IntlMessages id="ui-general-additional-parameters" />
                                </Title>
                              }
                            >
                              <DynamicFormDataFields
                                setFieldValue={setFieldValue}
                                fields={optionalFields}
                                values={values}
                                span={24}
                              />
                            </Panel>
                          </Collapse>
                        )}
                      </Col>
                    </Row>
                  ) : <Row gutter={16} className="hp-d-flex-justify-center"><Spin spinning /></Row>}
                </Col>
              </Row>

              {(dirty || hasUnpublishedChanges) && (
                <>
                  <Alert
                    message={<IntlMessages id="ui-general-edit-has-changes" />}
                    type="info"
                    showIcon
                  />

                  <Row justify='end' className='hp-mt-24 hp-mb-24'>
                    <Col>
                      <Row>
                        <IntlMessages id="ui-general-publish-changes" />
                        <Switch name="is_published" className='hp-ml-16' />
                      </Row>
                    </Col>
                  </Row>
                </>
              )}

              <Row gutter={[ 16, 16 ]} className='hp-mt-16' justify='end'>
                <Col>
                  <ResetButton
                    disabled={false}
                    onClick={() => resetForm(initialValues)}
                  >
                    <IntlMessages id='ui-general-reset' />
                  </ResetButton>
                </Col>

                <Col>
                  <SubmitButton
                    type="primary"
                    loading={isSubmitting}
                    disabled={!isValid || !dirty}
                  >
                    <IntlMessages id="ui-general-save" />
                  </SubmitButton>
                </Col>
              </Row>
            </Spinner>
          </Form>
        );
      }}
    </Formik>
  );
};

FormEditLocalNotification.propTypes = {
  initialErrors: PropTypes.object.isRequired,
  initialValues: PropTypes.object.isRequired,
  templateOptions: PropTypes.array.isRequired,
  templateFields: PropTypes.array.isRequired,
  validationSchema: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
};


export default FormEditLocalNotification;
